/**
 * Connection Pool Gauge Component
 *
 * Displays a circular gauge visualization of the connection pool utilization.
 * Features:
 * - Circular gauge with percentage display
 * - Color-coded based on utilization level (green/yellow/red)
 * - Real-time stats for active, idle, and total connections
 * - WCAG 2.1 Level AA accessible
 * - Uses Shadcn/ui components
 */

import * as React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, Activity, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConnectionPoolStats } from '../../../hooks/admin';

export interface ConnectionPoolGaugeProps {
  className?: string;
}

/**
 * GaugeChart - SVG-based circular gauge component
 */
interface GaugeChartProps {
  value: number;
  max: number;
  label: string;
  color: string;
}

function GaugeChart({ value, max, label, color }: GaugeChartProps) {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  const radius = 80;
  const strokeWidth = 16;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg height={radius * 2} width={radius * 2} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          stroke="hsl(var(--muted))"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        {/* Progress circle */}
        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference + ' ' + circumference}
          style={{ strokeDashoffset }}
          strokeLinecap="round"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
          className="transition-all duration-500 ease-in-out"
        />
      </svg>
      {/* Center text */}
      <div className="absolute flex flex-col items-center justify-center">
        <span className="text-3xl font-bold" style={{ color }}>
          {percentage.toFixed(1)}%
        </span>
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}

/**
 * ConnectionPoolGaugeSkeleton - Loading skeleton for the gauge
 */
function ConnectionPoolGaugeSkeleton() {
  return (
    <Card data-testid="connection-pool-gauge-skeleton">
      <CardHeader>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-4 w-64" />
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex justify-center">
          <Skeleton className="h-40 w-40 rounded-full" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * ConnectionPoolGauge - Main component
 *
 * Displays a circular gauge visualization of connection pool utilization
 * with real-time statistics.
 */
export function ConnectionPoolGauge({ className }: ConnectionPoolGaugeProps) {
  const { data, isLoading, isError, mutate } = useConnectionPoolStats();
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await mutate();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  if (isLoading) {
    return <ConnectionPoolGaugeSkeleton />;
  }

  if (isError || !data) {
    return (
      <Card className={className} data-testid="connection-pool-gauge-error">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Connection Pool</AlertTitle>
          <AlertDescription>
            Failed to load connection pool statistics. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      </Card>
    );
  }

  const { connection_pool } = data;

  // Determine color based on utilization
  const getUtilizationColor = (utilization: number): string => {
    if (utilization >= 80) return 'hsl(var(--destructive))'; // red
    if (utilization >= 60) return 'hsl(var(--warning))'; // orange/yellow
    return 'hsl(var(--success))'; // green
  };

  const utilizationColor = getUtilizationColor(connection_pool.utilization_pct);

  return (
    <Card className={className} data-testid="connection-pool-gauge">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" aria-hidden="true" />
            <CardTitle className="text-lg">Connection Pool</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label="Refresh connection pool statistics"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <CardDescription>Real-time connection pool utilization</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Gauge Chart */}
        <div className="flex justify-center py-4">
          <GaugeChart
            value={connection_pool.total}
            max={connection_pool.max}
            label="Utilization"
            color={utilizationColor}
          />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col items-center justify-center p-4 rounded-lg bg-muted/50">
            <span className="text-2xl font-bold text-foreground">{connection_pool.active}</span>
            <span className="text-xs text-muted-foreground">Active</span>
          </div>
          <div className="flex flex-col items-center justify-center p-4 rounded-lg bg-muted/50">
            <span className="text-2xl font-bold text-foreground">{connection_pool.idle}</span>
            <span className="text-xs text-muted-foreground">Idle</span>
          </div>
          <div className="flex flex-col items-center justify-center p-4 rounded-lg bg-muted/50">
            <span className="text-2xl font-bold text-foreground">{connection_pool.total}</span>
            <span className="text-xs text-muted-foreground">Total</span>
          </div>
        </div>

        {/* Connection Info */}
        <div className="flex justify-between text-sm text-muted-foreground pt-2 border-t">
          <span>Max Connections</span>
          <span className="font-medium text-foreground">{connection_pool.max}</span>
        </div>

        {/* Wait Queue Warning */}
        {connection_pool.wait_queue > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Connection Wait Queue</AlertTitle>
            <AlertDescription>
              {connection_pool.wait_queue} connection{connection_pool.wait_queue !== 1 ? 's' : ''}{' '}
              waiting. Consider increasing max_connections or optimizing queries.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
